import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { hasValidCoordinates } from "@shared/helpers/geo";
import { type FC } from "react";
import { FormGoogle } from "../../../components/form-components/form-google-autocomplete/FormGoogle";
import { FormSection } from "./partner-organization-form-ui";
import {
  emptyPartnerOrgAddressDraft,
  type PartnerOrgAddressDraft,
} from "./partner-organization-addresses";

type Props = {
  drafts: PartnerOrgAddressDraft[];
  onChange: (next: PartnerOrgAddressDraft[]) => void;
  onPickOnMap: (clientId: string) => void;
};

export const PartnerOrganizationAddressesEditor: FC<Props> = ({
  drafts,
  onChange,
  onPickOnMap,
}) => {
  const updateDraft = (
    clientId: string,
    patch: Partial<PartnerOrgAddressDraft>,
  ) => {
    onChange(
      drafts.map((draft) =>
        draft.clientId === clientId ? { ...draft, ...patch } : draft,
      ),
    );
  };

  const removeDraft = (clientId: string) => {
    const next = drafts.filter((draft) => draft.clientId !== clientId);
    onChange(next.length > 0 ? next : [emptyPartnerOrgAddressDraft()]);
  };

  return (
    <FormSection
      title="כתובות"
      description="אפשר כמה מיקומים לאותו ארגון. הראשונה היא הכתובת הראשית במפה ובטבלה."
      action={
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => onChange([...drafts, emptyPartnerOrgAddressDraft()])}
          sx={{ flexShrink: 0, textTransform: "none", borderRadius: 2 }}
        >
          כתובת נוספת
        </Button>
      }
    >
      <Stack gap={2}>
        {drafts.map((draft, index) => (
          <Box
            key={draft.clientId}
            sx={{
              p: 2.25,
              borderRadius: 2,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack gap={2}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                    {index === 0 ? "כתובת ראשית" : `כתובת ${index + 1}`}
                  </Typography>
                  {hasValidCoordinates(draft.coordinates) ? (
                    <Chip
                      size="small"
                      label="על המפה"
                      color="primary"
                      sx={{ height: 24, fontSize: 12, fontWeight: 600 }}
                    />
                  ) : draft.address.trim() ? (
                    <Chip
                      size="small"
                      label="בלי נקודה במפה"
                      variant="outlined"
                      sx={{ height: 24, fontSize: 12 }}
                    />
                  ) : null}
                </Stack>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeDraft(draft.clientId)}
                  aria-label="מחיקת כתובת"
                  disabled={drafts.length === 1 && !draft.address.trim()}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} gap={2}>
                <TextField
                  label="תווית (אופציונלי)"
                  placeholder="למשל סניף צפון, מחסן"
                  value={draft.label}
                  onChange={(e) =>
                    updateDraft(draft.clientId, { label: e.target.value })
                  }
                  sx={{ width: { xs: "100%", md: 240 } }}
                />
                <Stack
                  direction="row"
                  gap={1}
                  alignItems="flex-start"
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <FormGoogle
                    label="כתובת"
                    fullWidth
                    placeValue={draft.address}
                    onLocationChange={({ locationName, latLng }) =>
                      updateDraft(draft.clientId, {
                        address: locationName,
                        coordinates: hasValidCoordinates(latLng) ? latLng : null,
                      })
                    }
                  />
                  <Tooltip title="בחירת מיקום במפה">
                    <Button
                      variant="outlined"
                      onClick={() => onPickOnMap(draft.clientId)}
                      aria-label="בחירת מיקום במפה"
                      sx={{
                        minWidth: 48,
                        height: 56,
                        borderRadius: 2,
                        px: 1.5,
                      }}
                    >
                      <PlaceOutlinedIcon />
                    </Button>
                  </Tooltip>
                </Stack>
              </Stack>
            </Stack>
          </Box>
        ))}
      </Stack>
    </FormSection>
  );
};
